import { format, isValid } from "date-fns";
import { useEffect, useState } from "react";
import { DateRangePicker, DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { Button, IconButton, SxProps, TextField, Theme } from "@mui/material";
import useDetectScreenWidth from "@/modules/common/hooks/device-detection.hook";
import { staticRanges } from "@/modules/common/components/ui/date-picker/constants";

interface DatePickerProps {
  label: string;
  startDate: Date;
  endDate: Date;
  handleChange: (startDate: Date, endDate: Date) => void;
  handleClear?: () => void;
  defaultValue?: string;
  maxDate?: Date;
  minDate?: Date;
  sx?: SxProps<Theme>;
}

const style = {
  position: "absolute",
  display: "flex",
  flexDirection: "column",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  borderRadius: "8px",
  boxShadow: 24,
  p: 4,
};

export const formatDateSafely = (date: Date): string => {
  return isValid(date) ? format(date, "dd/MM/yyyy") : "";
};

const ReactDateRangePicker: React.FC<DatePickerProps> = ({
  label,
  startDate,
  endDate,
  handleChange,
  handleClear,
  defaultValue,
  maxDate,
  minDate,
  sx,
}) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(defaultValue);
  const [state, setState] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    },
  ]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleClearValue = () => {
    setValue("");
    setState([
      {
        startDate: new Date(),
        endDate: new Date(),
        key: "selection",
      },
    ]);
    if (handleClear) {
      handleClear();
    }
  };

  const deviceType = useDetectScreenWidth();

  const handleDateRangeChange = (item: any) => {
    const startDate = new Date(
      Date.UTC(
        item.selection.startDate.getFullYear(),
        item.selection.startDate.getMonth(),
        item.selection.startDate.getDate()
      )
    );
    const endDate = new Date(
      Date.UTC(
        item.selection.endDate.getFullYear(),
        item.selection.endDate.getMonth(),
        item.selection.endDate.getDate()
      )
    );

    setState([{ startDate, endDate, key: "selection" }]);
    const formattedStartDate = formatDateSafely(startDate);
    const formattedEndDate = formatDateSafely(endDate);

    if (formattedStartDate && formattedEndDate) {
      setValue(`${formattedStartDate} - ${formattedEndDate}`);
      handleChange(startDate, endDate);
    } else {
      setValue("");
      console.warn("Invalid date selected");
    }
  };

  useEffect(() => {
    if (startDate && endDate) {
      const formattedStartDate = formatDateSafely(startDate);
      const formattedEndDate = formatDateSafely(endDate);

      if (formattedStartDate && formattedEndDate) {
        setValue(`${formattedStartDate} - ${formattedEndDate}`);
      } else {
        setValue("");
      }
    }
  }, [value]);

  useEffect(() => {
    if (!defaultValue) setValue("");
  }, [defaultValue, value]);

  return (
    <>
      <TextField
        value={value || ""}
        fullWidth
        size="small"
        onFocus={handleOpen}
        label={label}
        InputProps={{
          endAdornment: (
            <>
              {defaultValue && (
                <IconButton
                  type="button"
                  aria-label="clear-value"
                  onClick={handleClearValue}
                >
                  <CloseOutlinedIcon />
                </IconButton>
              )}
              <IconButton
                type="button"
                aria-label="open-calendar"
                onClick={handleOpen}
              >
                <CalendarMonthOutlinedIcon />
              </IconButton>
            </>
          ),
          sx,
        }}
      />
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <>
          <Box
            sx={{ ...style, minHeight: { mobile: "480px", desktop: "530px" } }}
          >
            {deviceType.isMobile || deviceType.isTablet ? (
              // @ts-ignore
              <DateRange
                editableDateInputs={true}
                onChange={handleDateRangeChange}
                moveRangeOnFirstSelection={false}
                ranges={state}
                maxDate={maxDate}
                minDate={minDate}
              />
            ) : (
              // @ts-ignore
              <DateRangePicker
                onChange={handleDateRangeChange}
                moveRangeOnFirstSelection={false}
                ranges={state}
                direction="horizontal"
                months={2}
                staticRanges={staticRanges}
                maxDate={maxDate}
                minDate={minDate}
                inputRanges={[]}
              />
            )}
            <Box sx={{ display: "flex", justifyContent: "end", gap: 3 }}>
              <Button onClick={handleClearValue}>Clear</Button>
              <Button onClick={handleClose}>Apply</Button>
            </Box>
          </Box>
        </>
      </Modal>
    </>
  );
};

export default ReactDateRangePicker;
